import {Alert, Autocomplete, Group, SegmentedControl, Select, TextInput} from '@mantine/core'
import {DatePickerInput} from '@mantine/dates'
import type {UseFormReturnType} from '@mantine/form'
import {useState} from 'react'

import {JOB_NAME_HINT} from 'src/jobs/create-job-validation'
import {parseDateInputValue} from 'src/jobs/job-schedule-format'
import {JobTimePicker} from 'src/jobs/job-time-picker'
import type {CreateJobFormValues} from 'src/jobs/types'

import {pastStart} from './job-entry-logic'

type CategoryOption = {id: string; name: string; specificTypes: string[]}

type WhatWhenSectionProps = {
    form: UseFormReturnType<CreateJobFormValues>
    errors: Record<string, string>
    categories: CategoryOption[]
    needsPlannedEnd: boolean
    saveAttempts: number
}

export function WhatWhenSection({
    form,
    errors,
    categories,
    needsPlannedEnd,
    saveAttempts,
}: WhatWhenSectionProps) {
    const values = form.values
    const [rangeStart, setRangeStart] = useState<Date | null>(null)
    const [rangePendingSince, setRangePendingSince] = useState<number | null>(null)
    const severalDays = values.scheduling.severalDays === true
    const passed = pastStart(values.scheduling, new Date())
    const category = categories.find((option) => option.id === values.scheduling.serviceTypeId)
    const rangeError =
        rangePendingSince === saveAttempts || rangeStart ? undefined : errors['scheduling.endDate']

    function setScheduling(patch: Partial<CreateJobFormValues['scheduling']>) {
        form.setFieldValue('scheduling', {...form.getValues().scheduling, ...patch})
    }

    return (
        <>
            <Group grow align="flex-start">
                <Select
                    label="Category"
                    placeholder="Select category"
                    withAsterisk
                    searchable
                    data={categories.map((option) => ({value: option.id, label: option.name}))}
                    value={values.scheduling.serviceTypeId}
                    error={errors['serviceTypeId']}
                    onChange={(categoryId) =>
                        form.setValues({
                            serviceTypeId: categoryId,
                            specificType: '',
                            categoryPickedByRep: true,
                            scheduling: {...form.getValues().scheduling, serviceTypeId: categoryId},
                        })
                    }
                />
                <Autocomplete
                    label="Specific type"
                    placeholder={
                        category?.specificTypes.length ? 'Pick or type' : 'Type it (optional)'
                    }
                    data={category?.specificTypes ?? []}
                    value={values.specificType ?? ''}
                    onChange={(specificType) =>
                        form.setValues({specificType, categoryPickedByRep: true})
                    }
                />
            </Group>
            <TextInput
                label="Job name"
                description={errors['jobName'] ? undefined : JOB_NAME_HINT}
                withAsterisk
                {...form.getInputProps('jobName')}
            />
            <SegmentedControl
                aria-label="How many days"
                value={severalDays ? 'several' : 'one'}
                data={[
                    {value: 'one', label: 'One day'},
                    {value: 'several', label: 'Several days in a row'},
                ]}
                onChange={(choice) => {
                    setRangePendingSince(choice === 'several' ? saveAttempts : null)
                    setRangeStart(null)
                    setScheduling({
                        pattern: 'one_day',
                        severalDays: choice === 'several',
                        endDate: form.getValues().scheduling.startDate,
                    })
                }}
                style={{alignSelf: 'flex-start'}}
            />
            <Group grow align="flex-start">
                {severalDays ? (
                    <DatePickerInput
                        type="range"
                        label="Days"
                        withAsterisk
                        allowSingleDateInRange={false}
                        value={
                            rangeStart
                                ? [rangeStart, null]
                                : [values.scheduling.startDate, values.scheduling.endDate]
                        }
                        error={rangeError}
                        inputWrapperOrder={['label', 'description', 'error', 'input']}
                        popoverProps={{
                            zIndex: 400,
                            position: 'bottom-start',
                            middlewares: {flip: false, shift: true},
                        }}
                        onChange={([first, last]) => {
                            const startDate = parseDateInputValue(first)
                            const endDate = parseDateInputValue(last)
                            setRangeStart(startDate && endDate ? null : startDate)
                            if (startDate && endDate) {
                                setRangePendingSince(null)
                                setScheduling({startDate, endDate})
                            }
                        }}
                    />
                ) : (
                    <DatePickerInput
                        label="Date"
                        withAsterisk
                        value={values.scheduling.startDate}
                        onChange={(value) => {
                            const startDate = parseDateInputValue(value)
                            if (startDate) {
                                setScheduling({startDate, endDate: startDate})
                            }
                        }}
                    />
                )}
                <JobTimePicker
                    label="Start time"
                    placeholder="Any time, e.g. 5am"
                    value={values.scheduling.startTime ?? null}
                    onChange={(startTime) => setScheduling({startTime})}
                />
                {needsPlannedEnd || values.plannedEndDate ? (
                    <DatePickerInput
                        label="Planned end date"
                        description="Expected end of the rental"
                        withAsterisk={needsPlannedEnd}
                        clearable
                        {...form.getInputProps('plannedEndDate')}
                        value={values.plannedEndDate ?? null}
                        onChange={(value) =>
                            form.setFieldValue('plannedEndDate', parseDateInputValue(value))
                        }
                    />
                ) : null}
            </Group>
            {passed ? (
                <Alert color="yellow" variant="light" data-testid="starts-in-past">
                    {passed === 'date' ? 'Start date is in the past' : 'Start time has passed'}
                </Alert>
            ) : null}
        </>
    )
}
